import type { NextApiHandler, NextApiRequest, NextApiResponse } from 'next';

import { appLogger } from '@/utils/app-logger/appLogger';
import {
    TN_GRAPHQL_ERROR_CODE_HEADER,
    TN_GRAPHQL_SERVER_BUILD_HEADER,
} from '@/utils/constants/common';

// Static JSON import so webpack inlines the manifest and Next.js's file tracer
// carries it with the wrapper. Missing manifest = build-time compile error.
import defaultManifest from '../__generated/gql/persisted-documents.json';

/**
 * Server-side counterpart to the persisted-hash client transport in
 * `utils/request.ts`. The client sends a sha256 under
 * `extensions[HASH_EXTENSION_KEY]`; this wrapper rewrites the request to inject
 * the operation from the manifest before Apollo Server sees it.
 *
 * It's a Next.js API-handler wrapper rather than an Apollo plugin because
 * Apollo Server validates `query` presence upstream of every plugin hook —
 * hydrating from inside a plugin throws `BadRequestError` first.
 */

/** Extension code emitted when the client sends a hash the manifest does not contain. */
export const PERSISTED_QUERY_NOT_FOUND = 'PERSISTED_QUERY_NOT_FOUND';

/**
 * Extension code for a request that reached the server with no operation source
 * at all — no `query`, no persisted-document hash, no APQ extension. Our client
 * transports always send one of these, so this shape means the query string (or
 * POST body) was stripped or rewritten in transit by a proxy/middlebox.
 * CBSENTWEB-5121: this was the prod BAD_REQUEST storm — Apollo rejected these
 * with a sanitized "An internal error occurred" that hid the cause.
 */
export const MISSING_GRAPHQL_OPERATION = 'MISSING_GRAPHQL_OPERATION';

/**
 * Extension code for a GET whose `extensions` search parameter is present but
 * not valid JSON — the in-transit truncation/re-encoding signature.
 */
export const MALFORMED_GRAPHQL_REQUEST = 'MALFORMED_GRAPHQL_REQUEST';

/**
 * Bespoke extension key — deliberately NOT Apollo's `persistedQuery.sha256Hash`
 * (Apollo Server's built-in APQ detector would intercept that shape upstream of
 * this wrapper). Exported as the single source of truth: the client in
 * `utils/request.ts` imports the same constant when building the request body.
 */
export const HASH_EXTENSION_KEY = 'tnPersistedDocumentHash' as const;

const MANIFEST_SOURCE = '@tn/foundation/graphql/__generated/gql/persisted-documents.json';

export type PersistedDocumentsMiddlewareOptions = {
    /**
     * In-memory manifest override. Intended for tests that want to feed a small
     * map directly. Production callers rely on the compiled-in default from
     * `@tn/foundation/graphql/__generated/gql/persisted-documents.json`.
     */
    manifest?: Record<string, string>;
};

/**
 * Validate at load time and fail-fast. A broken manifest would return
 * `PERSISTED_QUERY_NOT_FOUND` for every hash-only request — a load-time throw
 * is caught by the deploy pipeline; there's no useful "graceful" degradation
 * shape for persisted queries.
 */
const validateManifest = (raw: unknown, source: string): Map<string, string> => {
    if (raw == null || typeof raw !== 'object') {
        appLogger.error('persisted-documents: manifest is not an object', {
            source,
            actualType: raw === null ? 'null' : typeof raw,
        });
        throw new Error(
            `persisted-documents: manifest at "${source}" is not an object. Run \`pnpm codegen\`.`,
        );
    }
    const entries = Object.entries(raw as Record<string, unknown>);
    if (entries.length === 0) {
        appLogger.error('persisted-documents: manifest is empty', { source });
        throw new Error(
            `persisted-documents: manifest at "${source}" is empty. Codegen produced no operations.`,
        );
    }
    for (const [hash, operation] of entries) {
        if (typeof operation !== 'string') {
            appLogger.error('persisted-documents: manifest entry is not a string', {
                source,
                hash,
                operationType: typeof operation,
            });
            throw new Error(
                `persisted-documents: manifest entry for hash "${hash}" is not a string.`,
            );
        }
    }
    return new Map(entries as Array<[string, string]>);
};

/** Builds `<mfe>@<version>+<commit12>` from the same environment values as `getMfeHealth`. */
const getServerBuildId = (): string => {
    const mfeName = process.env.NEXT_PUBLIC_MFE_NAME || 'unknown';
    const mfeVersion = process.env.NEXT_PUBLIC_MFE_VERSION || '0.0.0';
    const commit = (process.env.GIT_COMMIT || 'unknown').slice(0, 12);
    return `${mfeName}@${mfeVersion}+${commit}`;
};

// Diagnostic headers for browser RUM: the client SDK's `beforeSend` is synchronous
// and cannot read the response body, so the error code and the rejecting server
// build are exposed as headers. Same-origin fetch — no CORS expose needed.
// CBSENTWEB-5121.
const respondBadRequest = (res: NextApiResponse, code: string, message: string): void => {
    res.setHeader(TN_GRAPHQL_ERROR_CODE_HEADER, code);
    res.setHeader(TN_GRAPHQL_SERVER_BUILD_HEADER, getServerBuildId());
    res.status(400).json({
        errors: [{ message, extensions: { code } }],
    });
};

const respondNotFound = (res: NextApiResponse, hash: string): void => {
    respondBadRequest(
        res,
        PERSISTED_QUERY_NOT_FOUND,
        `Persisted query not found: ${hash}. Likely MFE bundle skew — ` +
            `client sent a hash the server manifest does not recognize.`,
    );
};

// Deliberately unsanitized (the middleware responds before Apollo, so the
// error-sanitization plugin never masks these): the message is what lands in
// client "API request failed" logs, and it has to name the probable cause.
const respondMissingOperation = (res: NextApiResponse): void => {
    respondBadRequest(
        res,
        MISSING_GRAPHQL_OPERATION,
        'GraphQL request arrived without a query, persisted-document hash, or persistedQuery ' +
            'extension. The client transport always sends one of these, so the request was ' +
            'likely stripped or rewritten in transit (proxy/middlebox).',
    );
};

const respondMalformedRequest = (res: NextApiResponse): void => {
    respondBadRequest(
        res,
        MALFORMED_GRAPHQL_REQUEST,
        'The extensions search parameter is not valid JSON. The request URL was likely ' +
            'truncated or rewritten in transit (proxy/middlebox).',
    );
};

// `@as-integrations/next` reads GET query strings from `req.url`, not from
// Next.js's parsed `req.query`. So we rewrite `req.url` for GET and mutate
// `req.body` for POST — those are the values the integration actually reads.

/**
 * Classified read of the `extensions` search parameter. The three failure
 * shapes matter independently: `absent` and `invalid` are the two in-transit
 * mangling signatures (param stripped vs truncated/re-encoded), while `valid`
 * without our hash may still carry Apollo's own APQ extension.
 */
type ParsedExtensions =
    | { kind: 'absent' }
    | { kind: 'invalid' }
    | { kind: 'valid'; hash?: string; hasApolloPersistedQuery: boolean };

const parseExtensionsParam = (searchParams: URLSearchParams): ParsedExtensions => {
    const raw = searchParams.get('extensions');
    if (raw == null) return { kind: 'absent' };
    try {
        const parsed = JSON.parse(raw) as unknown;
        if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
            return { kind: 'invalid' };
        }
        const record = parsed as Record<string, unknown>;
        const value = record[HASH_EXTENSION_KEY];
        return {
            kind: 'valid',
            hash: typeof value === 'string' ? value : undefined,
            hasApolloPersistedQuery: record.persistedQuery != null,
        };
    } catch {
        return { kind: 'invalid' };
    }
};

const rewriteGetUrl = (req: NextApiRequest, operation: string): void => {
    // `req.url` is a path + query (no scheme/host). Use a throwaway base so the
    // WHATWG URL parser accepts it; we serialise only `.pathname + .search`
    // back into `req.url`.
    const url = new URL(req.url ?? '/', 'http://internal.local');
    url.searchParams.set('query', operation);

    // Drop our extension key. If it was the only extension, remove the param
    // entirely so Apollo doesn't parse an empty extensions object.
    const rawExtensions = url.searchParams.get('extensions');
    if (rawExtensions) {
        try {
            const parsed = JSON.parse(rawExtensions) as Record<string, unknown>;
            delete parsed[HASH_EXTENSION_KEY];
            if (Object.keys(parsed).length === 0) {
                url.searchParams.delete('extensions');
            } else {
                url.searchParams.set('extensions', JSON.stringify(parsed));
            }
        } catch {
            // Malformed extensions — leave it as Apollo would surface a clearer error.
        }
    }

    req.url = `${url.pathname}${url.search}`;
};

const extractHashFromPost = (body: unknown): string | undefined => {
    const extensions = (body as { extensions?: Record<string, unknown> } | null | undefined)
        ?.extensions;
    const value = extensions?.[HASH_EXTENSION_KEY];
    return typeof value === 'string' ? value : undefined;
};

/**
 * True only for a parsed (plain-object) POST body that carries no operation
 * source at all: no non-empty `query`, no persisted-document hash (checked by
 * the caller), no APQ extension. Non-object bodies (unparsed/absent/batched
 * arrays) pass through — Apollo owns those errors.
 */
const isPostBodyMissingOperation = (body: unknown): boolean => {
    if (body == null || typeof body !== 'object' || Array.isArray(body)) return false;
    const record = body as { query?: unknown; extensions?: Record<string, unknown> };
    if (typeof record.query === 'string' && record.query.length > 0) return false;
    if (record.extensions?.persistedQuery != null) return false;
    return true;
};

const stripHashFromPostExtensions = (body: unknown): void => {
    const bodyObj = body as { extensions?: Record<string, unknown> } | null | undefined;
    if (!bodyObj?.extensions) return;
    delete bodyObj.extensions[HASH_EXTENSION_KEY];
    if (Object.keys(bodyObj.extensions).length === 0) {
        delete bodyObj.extensions;
    }
};

/**
 * Wraps a Next.js API handler (typically the one returned by
 * `startServerAndCreateNextHandler(server, ...)`) with persisted-document
 * resolution. On requests that carry `extensions.tnPersistedDocumentHash`, the
 * middleware:
 *
 * 1. Looks the hash up in the codegen manifest.
 * 2. On hit → injects `query` (into `req.query` for GET, `req.body` for POST),
 *    strips our extension key, and delegates to the wrapped Apollo handler.
 * 3. On miss → responds 400 with a `PERSISTED_QUERY_NOT_FOUND` error extension
 *    and never invokes Apollo.
 *
 * Requests without our extension pass through unchanged when they carry any
 * other operation source (inline `query`, Apollo APQ extension), so dev tools,
 * curl, and migration-period clients still work. Requests with NO operation
 * source at all are rejected here with a descriptive 400
 * (`MISSING_GRAPHQL_OPERATION` / `MALFORMED_GRAPHQL_REQUEST`) instead of
 * Apollo's sanitized, opaque BAD_REQUEST — see CBSENTWEB-5121.
 */
export const wrapWithPersistedDocuments = (
    apolloHandler: NextApiHandler,
    options: PersistedDocumentsMiddlewareOptions = {},
): NextApiHandler => {
    const source = options.manifest ? '<in-memory>' : MANIFEST_SOURCE;
    const manifest = validateManifest(options.manifest ?? defaultManifest, source);

    return async (req, res) => {
        const method = req.method?.toUpperCase();

        if (method === 'GET') {
            // Parse the *actual* URL that Apollo will read from, not Next.js's
            // pre-parsed `req.query` (see comment above `rewriteGetUrl`).
            const url = new URL(req.url ?? '/', 'http://internal.local');
            const extensions = parseExtensionsParam(url.searchParams);
            if (extensions.kind === 'valid' && extensions.hash) {
                const operation = manifest.get(extensions.hash);
                if (!operation) {
                    respondNotFound(res, extensions.hash);
                    return;
                }
                rewriteGetUrl(req, operation);
            } else if (!url.searchParams.get('query')) {
                // No usable operation source. Reject here with a descriptive,
                // unsanitized error instead of letting Apollo return an opaque
                // BAD_REQUEST ("An internal error occurred" after sanitization).
                // APQ-shaped extensions stay Apollo's codepath.
                if (extensions.kind === 'invalid') {
                    respondMalformedRequest(res);
                    return;
                }
                if (extensions.kind === 'absent' || !extensions.hasApolloPersistedQuery) {
                    respondMissingOperation(res);
                    return;
                }
            }
        } else if (method === 'POST') {
            const hash = extractHashFromPost(req.body);
            if (hash) {
                const operation = manifest.get(hash);
                if (!operation) {
                    respondNotFound(res, hash);
                    return;
                }
                (req.body as { query?: string }).query = operation;
                stripHashFromPostExtensions(req.body);
            } else if (isPostBodyMissingOperation(req.body)) {
                respondMissingOperation(res);
                return;
            }
        }

        return apolloHandler(req, res);
    };
};
